import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FAQ_ITEMS, buildFaq, faqPageSchema } from '../data/faq';
import { updatePageMeta, addStructuredData, addBreadcrumbSchema } from '../utils/seo';
import { metaFor, breadcrumbsFor } from '../seo/routeMeta';
import { getPricing } from '../utils/pricing';

/**
 * /faq — the visible answers, and the FAQPage JSON-LD built from the SAME
 * items (data/faq.js). The server renderer prints both into the raw HTML with
 * live prices and the live gallery policy; this page rebuilds them from the
 * same live facts, so the page, the schema and the raw HTML agree.
 *
 * Google requires FAQ structured data to correspond to content a visitor can
 * actually read; schema without a visible page is a guidelines violation. This
 * page is that content, and it earns its place independently — these are the
 * questions that come up on nearly every call.
 */
function FAQ() {
  const [open, setOpen] = useState(0);
  const [items, setItems] = useState(FAQ_ITEMS);

  useEffect(() => {
    updatePageMeta(metaFor('/faq'));
    addBreadcrumbSchema(breadcrumbsFor('/faq'));
    let cancelled = false;
    getPricing()
      .then(facts => { if (!cancelled && facts) setItems(buildFaq(facts)); })
      .catch(() => { /* bundled answers already showing */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    addStructuredData(faqPageSchema(items));
  }, [items]);

  return (
    <div style={{ background: '#f6f4ee', minHeight: '100vh', padding: 'var(--page-top-offset) 1.25rem 5rem' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <p style={{
          fontFamily: "'Work Sans', sans-serif", fontSize: '0.7rem', letterSpacing: '0.2em',
          textTransform: 'uppercase', color: '#b8860b', fontWeight: 600, margin: '0 0 0.75rem'
        }}>
          Common questions
        </p>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 500,
          fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 1.1, margin: '0 0 1rem', color: '#16181d'
        }}>
          Real estate photography, answered
        </h1>
        <p style={{
          fontFamily: "'Work Sans', sans-serif", fontSize: '1.02rem', lineHeight: 1.7,
          color: '#4b5563', margin: '0 0 2.5rem', maxWidth: '60ch'
        }}>
          Pricing, turnaround, service area and what to expect on shoot day. If your question
          isn't here, <Link to="/contact" style={{ color: '#b8860b' }}>send it over</Link> —
          it'll probably end up on this page.
        </p>

        <div style={{ borderTop: '1px solid #e2ddd1' }}>
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} style={{ borderBottom: '1px solid #e2ddd1' }}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  style={{
                    width: '100%', textAlign: 'left', background: 'none', border: 0,
                    padding: '1.15rem 2.5rem 1.15rem 0', cursor: 'pointer', position: 'relative',
                    fontFamily: "'Work Sans', sans-serif", fontSize: '1.02rem',
                    fontWeight: 600, color: '#16181d', lineHeight: 1.45
                  }}
                >
                  {item.q}
                  <span aria-hidden="true" style={{
                    position: 'absolute', right: '4px', top: '1.15rem',
                    fontSize: '1.3rem', color: '#b8860b', lineHeight: 1,
                    transform: isOpen ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s'
                  }}>
                    +
                  </span>
                </button>
                {isOpen && (
                  <p style={{
                    margin: '0 0 1.35rem', paddingRight: '2.5rem',
                    fontFamily: "'Work Sans', sans-serif", fontSize: '0.97rem',
                    lineHeight: 1.75, color: '#4b5563'
                  }}>
                    {item.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: '3rem', padding: '1.75rem', background: '#fffdf8',
          border: '1px solid #e2ddd1', borderLeft: '4px solid #d4af37', borderRadius: '12px'
        }}>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.3rem',
            margin: '0 0 0.5rem', color: '#16181d'
          }}>
            Ready to book?
          </h2>
          <p style={{
            fontFamily: "'Work Sans', sans-serif", fontSize: '0.95rem',
            color: '#4b5563', margin: '0 0 1.1rem', lineHeight: 1.65
          }}>
            Pick a package and a time online. Your first listing includes a free social reel,
            and every shoot is backed by a reshoot guarantee.
          </p>
          <Link
            to="/book"
            style={{
              display: 'inline-block', padding: '12px 26px', background: '#16181d',
              color: '#fff', textDecoration: 'none', borderRadius: '6px',
              fontFamily: "'Work Sans', sans-serif", fontWeight: 600, fontSize: '0.92rem'
            }}
          >
            Book a shoot
          </Link>
        </div>
      </div>
    </div>
  );
}

export default FAQ;
